import type { Phase } from "@offsite/shared";

/**
 * Beacon Phase-Pill: Mono Caps mit pulsing Live-Dot.
 * Phase-Label-Map gemäß briefing/07-copy-deck.md.
 */

export type BeaconPhase =
  | Phase
  | "showcase"
  | "done";

const PHASE_LABELS: Record<BeaconPhase, string> = {
  waiting: "WAITING",
  topics: "THEMEN · LIVE",
  voting: "VOTING · LIVE",
  "winner-reveal": "WINNER",
  grouping: "TEAM-ZUTEILUNG · LIVE",
  working: "WORKING",
  einfuehrung: "EINFÜHRUNG",
  abschluss: "ABSCHLUSS",
  submitted: "SUBMITTED",
  evaluating: "EVALUATING · KI",
  evaluated: "EVALUATED",
  showcase: "SHOWCASE",
  overnight: "OVERNIGHT",
  // Kein „TAG 2": Das war das zweitägige Vertriebs-Offsite, und selbst dort
  // liegt der Reveal inzwischen am Ende von Tag 1. Die Gremien-Sitzung dauert
  // 25 Minuten.
  reveal: "REVEAL",
  done: "DONE",
};

const LIVE_PHASES: ReadonlySet<BeaconPhase> = new Set([
  "topics",
  "voting",
  "grouping",
  "working",
  "evaluating",
  "reveal",
]);

/**
 * Deutsche Beschriftungen für Offsites, die siezen.
 *
 * „WAITING" und „WORKING" sind unter Kollegen in Ordnung. Ein Gremium liest auf
 * dem eigenen Handy lieber, was es bedeutet.
 */
const PHASE_LABELS_FORMAL: Partial<Record<BeaconPhase, string>> = {
  waiting: "GLEICH GEHT ES LOS",
  working: "IHRE EINGABE",
  submitted: "ABGESCHICKT",
  overnight: "DIE KI BAUT",
  reveal: "ERGEBNIS",
  done: "FERTIG",
};

export function phaseLabel(phase: BeaconPhase, formal = false): string {
  if (formal && PHASE_LABELS_FORMAL[phase]) {
    return PHASE_LABELS_FORMAL[phase] as string;
  }
  return PHASE_LABELS[phase] ?? String(phase).toUpperCase();
}

export function isLivePhase(phase: BeaconPhase): boolean {
  return LIVE_PHASES.has(phase);
}

interface PhasePillProps {
  phase: BeaconPhase;
  /** Override: zeige Pulse auch wenn Phase nicht in LIVE_PHASES. */
  live?: boolean;
  /** Siezendes Offsite: deutsche Beschriftung statt „WAITING"/„WORKING". */
  formal?: boolean;
}

export function PhasePill({ phase, live, formal }: PhasePillProps) {
  const showLive = live ?? isLivePhase(phase);
  return (
    <span className="phase-pill" data-phase={phase}>
      {showLive && <span className="dot dot--live" aria-hidden="true" />}
      <span className="phase-pill-label mono">{phaseLabel(phase, formal)}</span>
    </span>
  );
}
